/* eslint-disable id-length */

import { useEffect, useMemo, useState } from "react";
import {
  type Edge,
  useEdgesState,
  useNodesInitialized,
  useNodesState,
  useReactFlow,
  Node,
  XYPosition,
} from "@xyflow/react";

import { ElkNode, ElkExtendedEdge } from "elkjs/lib/elk-api";
import { EdgeData, NodeType, State, Step } from "~/lib/pipeline";
import Worker from "~/lib/CORSWorker";
import Bugsnag from "~/lib/Bugsnag";

type ElkSmoothStepEdgeType = Edge & {
  pathOptions: {
    points: XYPosition[];
  };
};

/**
 * Get the ELK instance, using a web worker to offload layout calculations
 * to a separate thread.
 */
const getElkInstance = (async function () {
  const ELK = (await import("elkjs/lib/elk-api")).default;
  return new ELK({
    workerFactory() {
      const corsWorker = new Worker(new URL("elkjs/lib/elk-worker", import.meta.url));
      return corsWorker.getWorker();
    },
  });
})();

// This number is used to determine when to disable the model order strategy to improve performance.
const MAX_NODE_SIZE_FOR_PRESERVED_ORDER = 0;

// Elk has a *huge* amount of options to configure. To see everything you can
// tweak check out:
//
// - https://www.eclipse.org/elk/reference/algorithms.html
// - https://www.eclipse.org/elk/reference/options.html
const elkOptions = {
  "elk.algorithm": "layered",

  "elk.layered.spacing.nodeNodeBetweenLayers": "30",
  "elk.layered.spacing.edgeNodeBetweenLayers": "30",
  "elk.layered.spacing.edgeEdgeBetweenLayers": "15",
  "elk.spacing.nodeNode": "15",
  "elk.spacing.edgeNode": "15",

  "elk.direction": "RIGHT",
  "elk.hierarchyHandling": "INCLUDE_CHILDREN",
  "elk.layered.nodePlacement.bk.fixedAlignment": "RIGHTDOWN",

  // Keep the nodes in the same order as defined in YAML.
  "elk.layered.crossingMinimization.forceNodeModelOrder": "true",
  "elk.layered.considerModelOrder.strategy": "NODES_AND_EDGES",

  "elk.layered.mergeEdges": "true",
};

type ExtendedEdge = ElkExtendedEdge & ElkSmoothStepEdgeType;
type ExtendedNode = ElkNode & Node;

/**
 * Layout the elements on the graph using the ELK library.
 */
export const layoutElements = async (
  nodes: Node[],
  edges: Edge[],
  options = {},
): Promise<{ nodes: Node[]; edges: Edge<EdgeData>[] }> => {
  const layoutOptions = {
    ...elkOptions,
    // If there are a large number of nodes, disable the model order strategy to improve performance.
    ...(nodes.length > MAX_NODE_SIZE_FOR_PRESERVED_ORDER
      ? {
          "elk.layered.crossingMinimization.forceNodeModelOrder": "false",
          "elk.layered.considerModelOrder.strategy": "NONE",
        }
      : {}),

    ...options,
  };

  // Unlike reactflow, Elk expects nodes to be nested under their parent.
  // The following assumes that parent nodes are always defined before their children.
  const children = Array.from(
    nodes
      .reduce((acc, node) => {
        // Keep track of the first-calculated height of the node.
        if (node.data.originalHeight === undefined) {
          // Add 1px buffer to avoid label truncation
          node.data.originalHeight = Math.ceil(node.measured?.height || 0) + 1;
        }

        // Keep track of the first-calculated width of the node.
        if (node.data.originalWidth === undefined) {
          // Add 1px buffer to avoid label truncation
          node.data.originalWidth = Math.ceil(node.measured?.width || 0) + 1;
        }

        node.width = node.data.originalWidth as number;
        node.height = node.data.originalHeight as number;

        if (node.parentId) {
          acc.get(node.parentId).children?.push(node);
          return acc;
        }

        acc.set(node.id, { ...node, children: [] });
        return acc;
      }, new Map())
      .values(),
  );

  const graph: ElkNode = {
    id: "root",
    layoutOptions,
    children: children.map((child) => {
      if (child.type === NodeType.Group) {
        // Find all the edges connected to group node children.
        const childEdges = child.children.reduce((acc, child) => {
          return [
            ...acc,
            ...edges.filter((edge) => edge.source === child.id || edge.target === child.id),
          ];
        }, []);

        return {
          ...child,
          children: child.children.map((child) => {
            return {
              ...child,
              layoutOptions: {
                // Align group child node to the left.
                "elk.alignment": "LEFT",
              },
            };
          }),
          layoutOptions: {
            // If there's no edges connected to the group children then we're safe to use an alternative algorithm.
            "elk.algorithm": childEdges.length === 0 ? "rectpacking" : "layered",

            // Aspect ratio is used to determine the width and height of the group node (used by the rectpacking algorithm).
            "elk.aspectRatio": 2.5,

            // When rendering a group node, the width and height of the node is determined by its children.
            // By adding padding to the group node, we can ensure the node is large enough to render it's
            // children and has space for the group label–ensuring edges stay aligned based on the calculated
            // width and height of the group node.
            //
            // Not the nicest solution since these values are hard-coded, but it works for now.
            "elk.padding": child.expandParent ? "[top=48,bottom=12,left=12,right=12]" : "",

            // Ensure group nodes have a minimum width to ensure label is visible and they align with other nodes.
            "elk.nodeSize.constraints": "MINIMUM_SIZE",
            "elk.nodeSize.minimum": `[${child.data.originalWidth}, ${child.data.originalHeight}]`,

            // Keep inner group spacing the same as outer spacing.
            "elk.layered.spacing.nodeNodeBetweenLayers": "30",
            "elk.layered.spacing.edgeNodeBetweenLayers": "30",
            "elk.spacing.nodeNode": "15",

            // Merge edges between group nodes.
            "elk.layered.mergeEdges": true,

            // Align group node to the left.
            "elk.alignment": "LEFT",
          },
        };
      }

      return {
        ...child,
        layoutOptions: {
          // Align node to the left.
          "elk.alignment": "LEFT",
        },
      };
    }),
    edges: edges.map((edge: Edge) => {
      return {
        ...edge,
      } as ExtendedEdge;
    }),
  };

  const elk = await getElkInstance;
  const layout = await elk
    .layout(graph)
    .then((layoutedGraph) => {
      const children = (layoutedGraph.children || []) as ExtendedNode[];

      // Flatten out the children for reactflow.
      const nodes = children.reduce<Map<string, ExtendedNode>>((acc, child) => {
        acc.set(child.id, child as ExtendedNode);

        if (child.children) {
          child.children.forEach((child) => {
            acc.set(child.id, child as ExtendedNode);
          });
        }
        return acc;
      }, new Map());

      return {
        // Apply Elk's layout to the reactflow nodes.
        nodes: Array.from(nodes.values()).map(({ width, height, ...node }) => {
          return {
            ...node,
            position: {
              x: node.x,
              y: node.y,
            },
            measured: {
              width: Math.ceil(width || 0),
              height: Math.ceil(height || 0),
            },
            style: {
              ...node.style,
              width: Math.ceil(width || 0),
              height: Math.ceil(height || 0),
            },
          };
        }),
        edges: layoutedGraph.edges?.map((edge) => {
          const sections = edge.sections || [];

          let points: XYPosition[] = sections.flatMap((section) => {
            if (section.bendPoints) {
              return [section.startPoint, ...section.bendPoints, section.endPoint];
            }
            return [section.startPoint, section.endPoint];
          });

          const source = nodes.get((edge as ExtendedEdge).source);
          const target = nodes.get((edge as ExtendedEdge).target);

          if (source?.parentId && source?.parentId === target?.parentId) {
            const parent = nodes.get(source.parentId);
            if (parent) {
              points = points.map(({ x, y }) => {
                return {
                  x: x + (parent.x || 0),
                  y: y + (parent.y || 0),
                };
              });
            }
          }

          return {
            ...edge,
            pathOptions: {
              points: points.map(({ x, y }) => ({
                x: Math.ceil(x),
                y: Math.ceil(y),
              })),
            },
          };
        }) as ExtendedEdge[],
      };
    })
    .catch((error) => {
      console.error(error);
      return { nodes, edges };
    });

  return {
    nodes: layout.nodes as Node[],
    edges: layout.edges as Edge<EdgeData>[],
  };
};

const TEMP_NODE_ID = "temp";

/**
 * This hook is responsible for positioning Reactflow nodes and edges.
 *
 * It uses the ELK library to calculate the layout of the graph but before it can do that,
 * it first needs to know the dimensions of the nodes. This is done by rendering the nodes in a hidden
 * state, calculating their dimensions, and then rendering the nodes in their final position.
 */
export default function usePositionedGraph({
  nodes: initialNodes,
  edges: initialEdges,
  onInitialised,
  expandedGroupSteps = new Set(),
  currentStep,
}: {
  nodes: Node[];
  edges: Edge[];
  onInitialised: () => void;
  expandedGroupSteps?: Set<string>;
  currentStep?: Step | null;
}) {
  const [nodes, setNodes, onNodesChange] = useNodesState<Node>([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState<Edge<EdgeData>>([]);
  const [initialised, setInitialised] = useState(false);
  const [calculatingLayout, setCalculatingLayout] = useState(false);

  const nodesInitialized = useNodesInitialized();

  const { getNodes, getNode, getEdge } = useReactFlow();
  const [customElkOptions, setOptions] = useState({});

  const stepsAwaitingInput = useMemo(() => {
    return initialNodes.filter(
      (node) =>
        (node.type === NodeType.Block || node.type === NodeType.Input) &&
        node.data.state !== State.Finished,
    ).length;
  }, [initialNodes]);

  // Hash the edges to detect changes and avoid unnecessary re-renders and re-layouts of the graph.
  const edgesHash = useMemo(() => {
    return JSON.stringify(initialEdges.map((edge) => ({ id: edge.id })));
  }, [initialEdges]);

  // Hash the nodes to detect changes and avoid unnecessary re-renders of the graph.
  const nodesHash = useMemo(() => {
    return JSON.stringify({ initialNodes });
  }, [initialNodes]);

  useEffect(() => {
    // Expose a function to allow Buildkite developers the ability tweak the graph layout via the console.
    // e.g. `configureGraphLayout({ "elk.direction": "DOWN" })`
    globalThis.configureGraphLayout = (options = {}) => {
      setOptions(options);
    };
  }, []);

  // When edges change (ie. there's a new dependency graph) or a group is expanded/collapsed, force the graph to reinitialize
  // which in turn will force the graph to re-layout.
  //
  // Re-calculating the layout can be expensive, so we only want to do it when necessary.
  //
  // This is a bit of a hack to force the graph to trigger the `nodesInitialized` event
  // which lets us know when node dimensions have been calculated. The problem with this
  // event is that it only fires when nodes are _added_ to the graph, so we add a temp node
  // to trigger the event which we subsequently remove before calculating node and edge positions.
  useEffect(() => {
    setCalculatingLayout(true);

    initialNodes.push({
      id: TEMP_NODE_ID,
      type: NodeType.Command,
      style: { visibility: "hidden" },
      data: {},
      position: { x: 0, y: 0 },
    });
  }, [edgesHash, stepsAwaitingInput, expandedGroupSteps.size]);

  // This useEffect updates ReactFlow with all of the nodes so that dimensions are calculated which will
  // later be used by Elk to layout and position the nodes.
  //
  // Some things to note:
  // - New nodes are invisible and subsequently made visible once they've been laid out.
  // - Existing nodes are updated with the latest data.
  // - Nodes being removed from the graph (ie. collapsed children), are removed post-layout.
  useEffect(() => {
    const nodes = new Map(getNodes().map((node) => [node.id, node]));

    initialNodes.forEach((nextNode) => {
      const prevNode = nodes.get(nextNode.id);

      if (!prevNode) {
        return nodes.set(nextNode.id, {
          ...nextNode,
          style: {
            ...nextNode.style,
            visibility: "hidden",
          },
        });
      }
      nodes.set(nextNode.id, {
        ...prevNode,
        data: {
          ...prevNode.data,
          ...nextNode.data,
        },
      });
    });

    setNodes(Array.from(nodes.values()));
  }, [edgesHash, nodesHash, stepsAwaitingInput]);

  // Only once nodes have been initialized (ie. dimensions calculated) we can layout the graph.
  useEffect(() => {
    if (!nodesInitialized) {
      return;
    }

    // Merge updated edge data with existing edges.
    const edges = initialEdges.map((edge) => {
      const existing = getEdge(edge.id);
      if (existing) {
        return {
          ...existing,
          ...edge,
          data: {
            ...existing.data,
            ...edge.data,
          },
        };
      }
      return edge;
    });

    // Merge updated node data with existing nodes.
    const nodes = initialNodes
      .filter((node) => node.id !== TEMP_NODE_ID)
      .map((node) => {
        const current = getNode(node.id);
        if (!current) {
          Bugsnag.notify(`Node ${node.id} not found`);
          return node;
        }

        return {
          ...current,
          ...node,
          data: {
            ...current.data,
            ...node.data,
          },
        };
      });

    layoutElements(nodes, edges, customElkOptions).then(({ nodes, edges }) => {
      nodes.forEach((node) => {
        node.selected = false;
        node.style = {
          ...node.style,
          visibility: "visible",
        };
      });

      // Flip the selected node based on the visibility of the current step to handle the case
      // where a group step is collapsed containing the current step.
      const nodesMap = new Map(nodes.map((node) => [node.id, node]));
      if (currentStep?.uuid) {
        const node = nodesMap.get(currentStep.uuid || "");
        const groupNode = nodesMap.get(currentStep.groupUuid || "");

        if (node) {
          node.selected = true;
        } else if (groupNode) {
          groupNode.selected = true;
        }
      }

      setCalculatingLayout(false);
      setNodes(nodes);
      setEdges(edges);

      if (!initialised && nodes.length) {
        onInitialised();
        setInitialised(true);
      }
    });
  }, [nodesInitialized, customElkOptions]);

  return {
    nodes,
    edges,
    initialised,
    calculatingLayout,
    onNodesChange,
    onEdgesChange,
  };
}
